import { useMutation } from '@tanstack/react-query';

import axios, { parseAxiosError } from '@/portainer/services/axios';
import { EnvironmentId } from '@/react/portainer/environments/types';
import { queryClient, withGlobalError } from '@/react-tools/react-query';

import { MicroK8sAddNodesFormValues } from '../NodeCreateView/types';
import { queryKeys } from '../queries/query-keys';

// remove nodes uses the internal portainer API to remove a node from a microk8s cluster
async function removeNodes(
  environmentId: EnvironmentId,
  nodesToRemove: string[]
) {
  try {
    await axios.post(`/cloud/endpoints/${environmentId}/nodes/remove`, {
      nodesToRemove,
    });
  } catch (e) {
    throw parseAxiosError(e, 'Unable to remove nodes');
  }
}

// useRemoveNodesMutation is used to remove a node from a microk8s cluster
export function useRemoveNodesMutation(environmentId: EnvironmentId) {
  return useMutation(
    (nodesToRemove: string[]) => removeNodes(environmentId, nodesToRemove),
    {
      ...withGlobalError('Unable to remove nodes.'),
      onSuccess: () =>
        queryClient.invalidateQueries(queryKeys.nodes(environmentId)),
    }
  );
}

async function addNodes(
  environmentId: EnvironmentId,
  addNodesValues: MicroK8sAddNodesFormValues // the formvalues and the request payload match
) {
  try {
    await axios.post(
      `/cloud/endpoints/${environmentId}/nodes/add`,
      addNodesValues
    );
  } catch (e) {
    throw parseAxiosError(e, 'Unable to add nodes');
  }
}

// useAddNodesMutation is used to nodes to an existing microk8s cluster
export function useAddNodesMutation(environmentId: EnvironmentId) {
  return useMutation(
    (addNodesValues: MicroK8sAddNodesFormValues) =>
      addNodes(environmentId, addNodesValues),
    {
      ...withGlobalError('Unable to add nodes.'),
      onSuccess: () =>
        queryClient.invalidateQueries([
          'environments',
          environmentId,
          'kubernetes',
          'nodes',
        ]),
    }
  );
}
